import React, { useState, MouseEvent, useMemo, useEffect, useRef } from 'react';
import styles from './lessonSection.module.scss';
import RightIcon from '@src/assets/icons/chewron_right.svg?react';
import ChevronDown from '@src/assets/icons/chevron_down.svg?react';
import { ContentType, LastVisitedLessonContentFragment as LastVisitedLesson, SllLesson, SllModule } from 'src/api/graphql-codegen';
import SectionContents from './section-contents/SectionContents';
import { useStandaloneContent } from '@src/hooks/useStandaloneContent';

type Props = {
  title: string;
  contents: SllModule[];
  lesson: SllLesson;
  lastVisitedLesson?: LastVisitedLesson;
  trackEventName?: string;
  closeNav?: () => void;
};

function LessonSection(props: Props) {
  const { title, contents = [], lesson, lastVisitedLesson, closeNav } = props;

  const [opened, setOpened] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const [progressWidth, setProgressWidth] = useState(0);
  const {
    content: selectedContent,
    lesson: selectedLesson,
    isPreProgramQuiz,
    quizModules,
    isNewLessonLayout,
    section,
    startedAlternateLesson,
    setLastContent,
  } = useStandaloneContent();

  const handleToggle = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const _opened = !opened;
    setOpened(_opened);
  };

  useEffect(() => {
    if (lastVisitedLesson && lastVisitedLesson?.lessonId === lesson.id) {
      setOpened(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastVisitedLesson]);

  useEffect(() => {
    if (selectedContent && contents.map(c => c.id).includes(selectedContent.id)) {
      setOpened(true);
    }
  }, [selectedLesson, lesson, selectedContent, contents]);

  const wrapperClassName = useMemo(() => {
    const classes = [styles.wrapper];
    if (opened) {
      classes.push(styles.opened);
    }

    if (contents.find(c => c.id === selectedContent?.id) && selectedLesson?.id === lesson.id) {
      classes.push(styles.selected);
    }

    return classes.join(' ');
  }, [opened, selectedContent, contents, lesson, selectedLesson]);

  const modules = useMemo(() => {
    // Alternate Lesson
    if (section && section.modules?.find(item => item?.id === selectedContent?.id)) {
      return section.modules
    }
    // Main Lesson
    if (lesson && lesson.presentation?.find(item => item?.id === selectedContent?.id)) {
      const preExitTicket = lesson.presentation.find(module => module?.name.toLowerCase().includes("exit ticket") && module.type === ContentType.SLIDE)
      const exitTicket = lesson.presentation.find(module => module?.name.toLowerCase().includes("exit ticket") && module.type === ContentType.FORM)
      if(preExitTicket && exitTicket) {
        return lesson.presentation.filter(module => module?.lastInLesson !== true && module !== preExitTicket); // Remove "Lesson Complete" and "PreExitTicket" module on Progress
      }
      return lesson.presentation.filter(module => module?.lastInLesson !== true); // Remove Lesson Complete module on Progress
    // Pre-Program Quiz Modules
    } else if (isPreProgramQuiz && quizModules) {
      const normalModules = quizModules.filter(content => content.type !== ContentType.FORM)
      const quizModule = quizModules.filter(content => content.type === ContentType.FORM)
      const modules = [...normalModules, quizModule[0]]
      return modules
    } else {
      return []
    }
  }, [selectedContent, lesson, section, isPreProgramQuiz, quizModules])
  // check which one is class presentation
  const moduleIndex = useMemo(() => {
    // Alternate Lesson
    if (section && section.modules?.find(item => item?.id === selectedContent?.id)) {
      return section.modules.findIndex(m => m?.id === selectedContent?.id);
    }
    // Pre-Program Quiz Modules
    if (isPreProgramQuiz && quizModules) {
      return quizModules.findIndex(m => m?.id === selectedContent?.id);
    }
    // Main Lesson
    if (lesson && lesson.presentation?.find(item => item?.id === selectedContent?.id)) {
      const progressModules = lesson.presentation.filter(module => module?.lastInLesson !== true);
      const lessonCompleteModule = lesson.presentation.find(module => module?.lastInLesson === true);
      if (lessonCompleteModule && lessonCompleteModule.id === selectedContent?.id) {
        return lesson.presentation.findIndex(module => module === lessonCompleteModule);
      }
      return progressModules.findIndex(m => m?.id === selectedContent?.id);
    }
    return -1
  }, [selectedContent, lesson, section, isPreProgramQuiz, quizModules])

  useEffect(() => {
    // Alternate Lesson
    if (startedAlternateLesson && section && section.modules?.find(item => item?.id === selectedContent?.id) && progressRef.current) {
      var contentIndex = modules.findIndex(m => m?.id === selectedContent?.id);
      if(contentIndex > 0) {
        if(contentIndex < modules.length - 1) {
          setProgressWidth((progressRef.current.clientWidth) * contentIndex / (modules.length - 1));
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        } else {
          setProgressWidth(0)
          setLastContent(false);
        }
      }
    } // Main Lesson
    else if (lesson && lesson.presentation?.find(item => item?.id === selectedContent?.id) && progressRef.current) {
      var lessonmodules = lesson.presentation.filter(module => module?.lastInLesson !== true);
      const preExitTicket = lessonmodules.find(module => module?.name.toLowerCase().includes("exit ticket") && module.type === ContentType.SLIDE)
      const exitTicket = lessonmodules.find(module => module?.name.toLowerCase().includes("exit ticket") && module.type === ContentType.FORM)
      const lessonCompleteModule = lesson.presentation.find(module => module?.lastInLesson === true);

      var moduleIndex = modules.findIndex(m => m?.id === selectedContent?.id);
      if (preExitTicket && exitTicket && preExitTicket.id === selectedContent?.id) {
        moduleIndex = modules.findIndex(m => m?.id === exitTicket.id)
      } else if (lessonCompleteModule && lessonCompleteModule.id === selectedContent?.id) {
        moduleIndex = lesson.presentation.findIndex(m => m?.id === lessonCompleteModule.id)
      }
      if(moduleIndex > 0) {
        if(moduleIndex < modules.length - 1) {
          setProgressWidth((progressRef.current.clientWidth) * moduleIndex / (modules.length - 1));
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        } else {
          setProgressWidth(0)
          setLastContent(false);
        }
      }
    }
     // Quiz Progress 
     else if (isPreProgramQuiz && quizModules && quizModules.find(item => item?.id === selectedContent?.id) && progressRef.current) {
      var quizIndex = modules.findIndex(m => m?.id === selectedContent?.id);
      if(quizIndex > 0) {
        if(quizIndex < modules.length - 1) {
          setProgressWidth((progressRef.current.clientWidth) * quizIndex / (modules.length - 1));
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        } else {
          setProgressWidth(0)
          setLastContent(false);
        }
      }
    }
  },[selectedContent, startedAlternateLesson, lesson, section, modules, setLastContent, isPreProgramQuiz, quizModules])
  
  return (
    <div className={wrapperClassName}>
      {!isPreProgramQuiz && <div onClick={handleToggle} className={styles.header}>
        <RightIcon className={styles.rightIcon}/>
        <div className={styles.title}>{title}</div>
        <div className={[styles.dropdownToggle, opened ? styles.dropdownToggleClose : styles.dropdownToggleOpen].join(' ')}>
          <ChevronDown className={styles.icon}/>
        </div>
      </div>}
      {isNewLessonLayout ? 
      <div ref={progressRef} className={styles.progressBackBar}>
        <div className={styles.progressBar} style={{width: `${progressWidth}px` }}>
        </div>
      </div> : null}
      <div className={styles.content}>
        <SectionContents contents={contents} lesson={lesson} lastVisitedLesson={lastVisitedLesson} closeNav={closeNav} selectedModuleIndex={moduleIndex} />
      </div>
    </div>
  );
}

export default React.memo(LessonSection);
